<template>
  <UIInputDynamicList
    v-model="modelValue"
    :data
    :disabled
    :placeholder
    @click:new="handleClickNew"
    @click:item="handleClickItem"
    @ready="(el: HTMLInputElement) => (componentRef = el)"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'

import { TagClass } from '@/entities/tags'

import { TAG_FIELD } from '@/entities/tags/utils/const'

import { getOriginalValues } from './utils/helpers'
import { handleCatchedError } from '@/helpers/common'

import { useTagsBunchStore } from '@/store/tags/bunch'

import { UIInputDynamicList } from '@ui/fields'

type Props = {
  tagName?: string
  assetId: string
  loading?: boolean
  focusOnLoad?: boolean
}

type Emits = {
  'value:assign': [tag: TagClass, callback: () => void]
  'value:select': [tag: TagClass]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineExpose({
  focus() {
    componentRef.value?.focus()
  },
})

const modelValue = defineModel<string | undefined>({ default: undefined })

const tagsBunchStore = useTagsBunchStore()

const componentRef = ref<HTMLInputElement>()

const loading = ref(false)

const disabled = computed(() => props.loading || !props.tagName || loading.value)

const placeholder = computed(() =>
  props.tagName ? 'Tag value' : 'Select tag name first',
)

const tagsList = computed(() => Array.from(tagsBunchStore.getCommonList))

const data = computed(() => {
  if (!props.tagName) return []
  const filteredData = tagsList.value.filter(
    ([, instance]) =>
      !props.tagName ||
      instance.field(TAG_FIELD.TAG_NAME).value === props.tagName,
  )
  const mappedData = filteredData.map(
    ([, instance]) => instance.field<string>(TAG_FIELD.TAG_VALUE).value,
  )
  return getOriginalValues(mappedData)
})

const handleClickNew = async (value: string, callback: () => void) => {
  if (!props.tagName) return
  await handleCreate({ value })
  callback()
}

const handleClickItem = async () => {
  await nextTick()
  if (!modelValue.value) return
  const tag = tagsBunchStore.getElementByPair(props.tagName, modelValue.value)
  if (!tag) return
  emit('value:select', tag)
}

const handleCreate = async (data?: { name?: string; value?: string }) => {
  try {
    loading.value = true
    const tag_name = data?.name || props.tagName
    const tag_value = data?.value || modelValue.value
    if (!tag_name || !tag_value) {
      loading.value = false
      return
    }
    const tag = tagsBunchStore.createElement()
    tag.set({ tag_name, tag_value, assets: [props.assetId] })
    await tag.store()
    emit('value:assign', tag, () => {
      loading.value = false
    })
  } catch (e) {
    loading.value = false
    handleCatchedError(e as string)
  }
}

watch(() => props.tagName, () => {
  modelValue.value = undefined
})

watch(componentRef, el => {
  if (!props.focusOnLoad) return
  el?.focus()
}, { immediate: true })
</script>
